import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, ClipboardCheck, Loader2, RotateCcw, AlertTriangle,
  Send, Hourglass, X,
} from "lucide-react";

/**
 * TRCA EPC Assessment — organisation-level. Per trca_assessment_and_sidebar_spec.md.
 *   - 5 categories × 5 questions × 5 multi-select options × 0.8 pts = 100
 *   - Per-user (not per-tender)
 *   - Retake is ADMIN-GATED (request → admin approves → user can retake)
 */

const PILL_FOR = (c) => {
  if (c === "dark_green") return { bg: "bg-good-soft", text: "text-good", border: "border-good/40" };
  if (c === "green")       return { bg: "bg-good-soft/70", text: "text-good", border: "border-good/30" };
  if (c === "amber")       return { bg: "bg-warn-soft", text: "text-warn", border: "border-warn/40" };
  if (c === "orange")      return { bg: "bg-warn-soft/90", text: "text-warn-strong", border: "border-warn/60" };
  if (c === "red")         return { bg: "bg-red-50", text: "text-red-700", border: "border-red-300" };
  return { bg: "bg-secondary", text: "text-ink", border: "border-ink-line" };
};

export default function TrcaEpcModule() {
  const [schema, setSchema] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [retakeReq, setRetakeReq] = useState(null);
  const [archivedCount, setArchivedCount] = useState(0);
  const [view, setView] = useState("loading"); // loading | intro | quiz | results
  const [currentQIdx, setCurrentQIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Flat list of questions
  const flatQuestions = useMemo(() => {
    if (!schema) return [];
    const list = [];
    schema.categories.forEach((cat) => {
      cat.questions.forEach((q) => list.push({ ...q, categoryLabel: cat.label, categoryId: cat.id }));
    });
    return list;
  }, [schema]);

  const load = async () => {
    try {
      const [{ data: sch }, { data: latest }] = await Promise.all([
        api.get("/trca-epc/schema"),
        api.get("/trca-epc"),
      ]);
      setSchema(sch);
      setArchivedCount(latest.archived_count || 0);
      setRetakeReq(latest.retake_request || null);
      if (latest.assessment) {
        setAssessment(latest.assessment);
        setView(latest.assessment.status === "completed" ? "results" : "quiz");
      } else {
        setView("intro");
      }
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      setView("intro");
    }
  };
  useEffect(() => { load(); }, []);

  const startAssessment = async () => {
    try {
      const { data } = await api.post("/trca-epc/start");
      setAssessment(data);
      setCurrentQIdx(0);
      setView("quiz");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const toggleOption = async (qCode, optIdx) => {
    if (!assessment) return;
    const current = assessment.answers?.[qCode] || [];
    const next = current.includes(optIdx) ? current.filter((i) => i !== optIdx) : [...current, optIdx].sort((a, b) => a - b);
    try {
      const { data } = await api.patch("/trca-epc/answers", { answers: { [qCode]: next } });
      setAssessment(data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/trca-epc/submit", { answers: {} });
      setAssessment(data);
      setView("results");
      toast.success(`TRCA EPC submitted · Score ${data.total_score} / 100`);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setSubmitting(false); }
  };

  const requestRetake = async () => {
    try {
      const { data } = await api.post("/trca-epc/request-retake");
      setRetakeReq(data);
      toast.success("Retake requested. An admin will review.");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const consumeApprovedRetake = async () => {
    try {
      await api.post("/trca-epc/retake");
      await load();
      setView("quiz");
      setCurrentQIdx(0);
      toast.success("Starting a fresh TRCA EPC attempt.");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  if (view === "loading" || !schema) {
    return <div className="p-10 text-sm text-ink-muted uppercase tracking-wider" data-testid="trca-epc-loading">Loading TRCA EPC…</div>;
  }
  if (view === "intro") return <IntroScreen onStart={startAssessment} archivedCount={archivedCount} />;
  if (view === "results" && assessment) return (
    <ResultsScreen
      assessment={assessment}
      retakeReq={retakeReq}
      onRequestRetake={requestRetake}
      onConsumeApprovedRetake={consumeApprovedRetake}
    />
  );
  if (view === "quiz" && assessment) {
    return (
      <QuizScreen
        questions={flatQuestions}
        answers={assessment.answers || {}}
        currentIdx={currentQIdx}
        onIdx={setCurrentQIdx}
        onToggle={toggleOption}
        onSubmit={submit}
        submitting={submitting}
        totalScore={assessment.total_score || 0}
      />
    );
  }
  return null;
}

/* -------------------------------------------------------------- INTRO ------ */
function IntroScreen({ onStart, archivedCount }) {
  return (
    <div className="max-w-3xl mx-auto py-12 px-4 animate-fade-up" data-testid="trca-epc-intro">
      <div className="text-[11px] uppercase tracking-[0.25em] text-ink-muted mb-3">Organisation Assessment</div>
      <h1 className="text-4xl font-display font-bold tracking-tight mb-3">TRCA EPC Assessment</h1>
      <p className="text-sm text-ink-muted leading-relaxed max-w-2xl">
        Tendering Readiness & Capability Assessment for EPC firms. Measures your organisation's
        readiness across 5 dimensions: Compliance, Governance, Commercial, Delivery, and Execution.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
        <Stat label="Categories" value="5" />
        <Stat label="Questions" value="25" />
        <Stat label="Format" value="Multi-select" />
        <Stat label="Time" value="~10 min" />
      </div>
      <div className="mt-8 card-flat p-5 bg-secondary/50">
        <div className="flex items-start gap-3">
          <ClipboardCheck className="w-5 h-5 text-brand shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-display font-semibold mb-1">How scoring works</div>
            <ul className="text-ink-muted space-y-1 list-disc list-inside text-[13px]">
              <li>Each question has 5 options. Select all that apply.</li>
              <li>Each selected option adds its points (default <span className="font-num font-semibold">0.8</span>).</li>
              <li>Question max = 4.0 · Category max = 20 · Total max = <span className="font-num font-semibold">100</span>.</li>
              <li>Once submitted, retaking requires admin approval.</li>
            </ul>
          </div>
        </div>
      </div>
      {archivedCount > 0 && (
        <div className="mt-3 text-[11px] text-ink-muted uppercase tracking-wider">
          {archivedCount} prior attempt{archivedCount > 1 ? "s" : ""} archived
        </div>
      )}
      <button onClick={onStart} data-testid="trca-epc-start-btn"
        className="mt-6 px-6 py-3 bg-brand hover:bg-brand-hover text-white font-medium text-sm rounded-sm inline-flex items-center gap-2 transition-colors">
        Begin assessment <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="card-flat p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-ink-muted">{label}</div>
      <div className="font-num font-bold text-2xl mt-1">{value}</div>
    </div>
  );
}

/* -------------------------------------------------------------- QUIZ ------- */
function QuizScreen({ questions, answers, currentIdx, onIdx, onToggle, onSubmit, submitting, totalScore }) {
  const q = questions[currentIdx];
  const selected = answers[q.code] || [];
  const answeredCount = questions.filter((q) => (answers[q.code] || []).length > 0).length;
  return (
    <div className="max-w-3xl mx-auto py-8 px-4 animate-fade-up" data-testid="trca-epc-quiz">
      {/* Progress */}
      <div className="flex items-center justify-between mb-4 text-[11px] uppercase tracking-[0.18em] text-ink-muted">
        <div data-testid="trca-epc-progress">Question {currentIdx + 1} of {questions.length} · <span className="text-ink">{q.categoryLabel}</span></div>
        <div className="font-num">Score: <span className="font-bold text-brand">{totalScore}</span> / 100 · Answered {answeredCount}/{questions.length}</div>
      </div>
      <div className="h-1 bg-secondary rounded-sm overflow-hidden mb-6">
        <div className="h-full bg-brand transition-all" style={{ width: `${((currentIdx + 1) / questions.length) * 100}%` }} />
      </div>

      {/* Question */}
      <div className="card-flat p-7" data-testid={`trca-epc-q-${q.code}`}>
        <div className="text-[11px] text-ink-muted uppercase tracking-[0.2em] mb-2">{q.code} · {q.categoryLabel}</div>
        <h2 className="text-lg font-display font-bold tracking-tight mb-1">{q.label}</h2>
        <p className="text-sm text-ink-muted italic mb-5">{q.prompt}</p>
        <div className="space-y-2">
          {q.options.map((opt, i) => {
            const isSel = selected.includes(i);
            return (
              <button key={i} onClick={() => onToggle(q.code, i)}
                data-testid={`trca-epc-opt-${q.code}-${i}`}
                className={`w-full text-left p-3 border rounded-sm transition-colors flex items-start gap-3 ${
                  isSel ? "border-brand bg-brand-soft" : "border-ink-line hover:bg-secondary"
                }`}>
                <span className={`shrink-0 w-5 h-5 mt-0.5 border rounded-sm flex items-center justify-center ${
                  isSel ? "bg-brand border-brand text-white" : "border-ink-line bg-white"
                }`}>
                  {isSel && <Check className="w-3 h-3" />}
                </span>
                <span className="text-sm leading-relaxed">{opt}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-3 text-[11px] text-ink-muted">
          <span className="font-num">{selected.length}</span> of {q.options.length} selected · {(selected.reduce((a, i) => a + Number(q.option_scores?.[i] ?? 0.8), 0)).toFixed(1)} pts
        </div>
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between mt-6">
        <button onClick={() => onIdx(Math.max(0, currentIdx - 1))} disabled={currentIdx === 0}
          data-testid="trca-epc-prev"
          className="px-4 py-2 text-sm border border-ink-line rounded-sm hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>
        {currentIdx < questions.length - 1 ? (
          <button onClick={() => onIdx(currentIdx + 1)} data-testid="trca-epc-next"
            className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-2 transition-colors">
            Next <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={onSubmit} disabled={submitting} data-testid="trca-epc-submit"
            className="px-5 py-2 text-sm bg-good hover:bg-good/90 text-white font-medium rounded-sm flex items-center gap-2 transition-colors disabled:opacity-50">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <><Check className="w-4 h-4" /> Submit Assessment</>}
          </button>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- RESULTS --- */
function ResultsScreen({ assessment, retakeReq, onRequestRetake, onConsumeApprovedRetake }) {
  const a = assessment;
  const pill = PILL_FOR(a.tier_color);
  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6 animate-fade-up" data-testid="trca-epc-results">
      {/* Headline */}
      <div className={`card-flat p-8 border-l-4 ${pill.border}`} data-testid="trca-epc-result-headline">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="stat-label">TRCA EPC result</div>
            <div className="flex items-baseline gap-2 mt-2">
              <div className="font-num text-7xl font-bold leading-none" data-testid="trca-epc-total-score">{a.total_score}</div>
              <div className="text-2xl font-num text-ink-muted">/ 100</div>
            </div>
            <div className="mt-4">
              <span className={`px-3 py-1 text-sm font-display font-bold rounded-sm border ${pill.bg} ${pill.text} ${pill.border}`} data-testid="trca-epc-tier">
                {a.tier_label}
              </span>
            </div>
          </div>
          <RetakeArea retakeReq={retakeReq} onRequest={onRequestRetake} onConsume={onConsumeApprovedRetake} />
        </div>
      </div>

      {/* Category breakdown — percentages per spec §1.7 */}
      <div className="card-flat p-6">
        <div className="stat-label mb-4">Category breakdown</div>
        <div className="space-y-3" data-testid="trca-epc-breakdown">
          {(a.category_breakdown || []).map((c) => {
            const pct = c.pct ?? Math.round((c.score / c.max_score) * 100);
            return (
              <div key={c.id} className="space-y-1" data-testid={`trca-epc-cat-${c.id}`}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{c.label}</span>
                  <span className="font-num text-ink-muted" data-testid={`trca-epc-cat-pct-${c.id}`}>{pct}%</span>
                </div>
                <div className="h-2 bg-secondary rounded-sm overflow-hidden">
                  <div className="h-full bg-brand transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-[11px] uppercase tracking-wider text-ink-muted text-center">
        Submitted {a.completed_at?.slice(0, 10) || "—"} · Organisation-level assessment
      </div>
    </div>
  );
}

function RetakeArea({ retakeReq, onRequest, onConsume }) {
  if (!retakeReq) {
    return (
      <button onClick={onRequest} data-testid="trca-epc-request-retake"
        className="px-4 py-2 text-sm border border-ink-line hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors">
        <Send className="w-4 h-4" /> Request retake
      </button>
    );
  }
  if (retakeReq.status === "pending") {
    return (
      <div className="px-4 py-2 text-xs border border-warn/40 bg-warn-soft text-warn rounded-sm inline-flex items-center gap-2" data-testid="trca-epc-retake-pending">
        <Hourglass className="w-4 h-4" /> Retake requested · awaiting admin
      </div>
    );
  }
  if (retakeReq.status === "approved") {
    return (
      <button onClick={onConsume} data-testid="trca-epc-start-retake"
        className="px-4 py-2 text-sm bg-good hover:bg-good/90 text-white rounded-sm inline-flex items-center gap-2 transition-colors">
        <RotateCcw className="w-4 h-4" /> Start approved retake
      </button>
    );
  }
  if (retakeReq.status === "rejected") {
    return (
      <div className="px-4 py-2 text-xs border border-red-300 bg-red-50 text-red-700 rounded-sm inline-flex items-center gap-2">
        <X className="w-4 h-4" /> Retake rejected
      </div>
    );
  }
  return null;
}
